import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Download, Palette, Type, PenLine, Smartphone, Package,
  FileText, Image, ExternalLink, Copy, Check, Printer,
  ChevronDown, ChevronUp, Link2, Globe, MessageCircle, Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const SITE = process.env.REACT_APP_BACKEND_URL;

const LOGOS = [
  { name: "schedabusiness — Marchio Principale", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/dbdc3d6e1c39252d006c0263aee55f5435b0800715d0eb190855a984abad68b9.png" },
  { name: "SBPA — Digital Academy", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/bf3826606211f86b61b02b4db2373f48f304a8a0eba3f27fa52eebef60a2c70c.png" },
  { name: "Virtual Tour 360", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/f0a377102406c966d6d0dbddd1fe498101dc4e87d87ac212a879bb6e6da86bf9.png" },
];

const PENS = [
  { name: "Blu — Start", hex: "#3B82F6", pantone: "PMS 2727 C", rgb: "59, 130, 246", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/23eeda59e4b6716b81902356d7ad31d605f63a57a12df30faced2fd506cd0ded.png" },
  { name: "Rosso — Plus", hex: "#EF4444", pantone: "PMS 1797 C", rgb: "239, 68, 68", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/a5defb491e7193c8ac5a2c9179b045961fda09e83d39f27f1a945c30c451cca2.png" },
  { name: "Ambra — Rooftop", hex: "#F59E0B", pantone: "PMS 130 C", rgb: "245, 158, 11", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/1a3b8ef3b70d95cc117df0f81b770ffeb73da4315d4096d6f0f38d33c1897754.png" },
  { name: "Teal — Virtual Tour", hex: "#0D9488", pantone: "PMS 7473 C", rgb: "13, 148, 136", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/bd39066b9f3bce302c8ab913729500c56d0bafbd82fd91dce6e29efc0e4cd881.png" },
];

const COLORS_GOOGLE = [
  { name: "Blu Google", hex: "#4285F4", pantone: "Pantone 660 C", rgb: "66, 133, 244" },
  { name: "Rosso Google", hex: "#EA4335", pantone: "Pantone 1795 C", rgb: "234, 67, 53" },
  { name: "Giallo Google", hex: "#FBBC04", pantone: "Pantone 7549 C", rgb: "251, 188, 4" },
  { name: "Verde Google", hex: "#34A853", pantone: "Pantone 7481 C", rgb: "52, 168, 83" },
];

const COLORS_BRAND = [
  { name: "Arancio schedabusiness", hex: "#FF6B35", pantone: "Pantone 165 C", rgb: "255, 107, 53", use: "CTA principale, logo secondario" },
  { name: "Terre di Siena", hex: "#7B2D26", pantone: "—", rgb: "123, 45, 38", use: "Academy / Formazione" },
  { name: "Background chiaro", hex: "#FAF8F5", pantone: "—", rgb: "250, 248, 245", use: "Sfondo pagine web" },
];

const STAND_IMAGES = [
  { name: "Base Smartphone (close-up)", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/b71cf704d84e79a2e485dfbd389f625f88f2d81657315904b856aac6606f8373.png" },
  { name: "Base Smartphone (scrivania)", url: "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/ba755fa5311e973423ce087f42c08363f8ac45c3c8e460a3600e6b7da6745150.png" },
];

const ALL_PENS_IMG = "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/1af9598c3ab41814b03fadda5eb969e204577a2e529233163652411316dad110.png";
const BUNDLE_IMG = "https://static.prod-images.emergentagent.com/jobs/5efdd782-584e-41f8-94c7-665072b1a298/images/c09b4e3031a7b7a2e5f5938091d9040decdfd59d87e1fc36e253ac076d6f444f.png";

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1500); }}
      className="ml-1.5 text-gray-400 hover:text-gray-600 transition-colors print:hidden" title="Copia">
      {ok ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function DL({ url, label }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" download
      className="inline-flex items-center gap-1 text-[11px] text-[#ff6b35] hover:text-[#e55a2b] font-semibold transition-colors print:text-gray-600 print:no-underline">
      <Download className="w-3 h-3" /> {label || "Scarica"}
    </a>
  );
}

function Sec({ title, icon: Icon, children, id }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-7 break-inside-avoid" id={id}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-2.5 border-b-2 border-[#ff6b35] mb-3 text-left print:pointer-events-none">
        <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2 font-google">
          <Icon className="w-4 h-4 text-[#ff6b35]" /> {title}
        </h2>
        <span className="print:hidden">{open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function LinkRow({ href, label, detail }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-3 py-2.5 bg-white rounded-xl border border-gray-200 hover:border-[#ff6b35]/40 transition-colors group" data-testid={`bk-link-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-2">
        <ExternalLink className="w-3.5 h-3.5 text-[#ff6b35] flex-shrink-0" />
        <span className="text-xs font-bold text-gray-800">{label}</span>
      </div>
      <span className="text-[10px] text-gray-400 group-hover:text-[#ff6b35] transition-colors truncate ml-2 max-w-[200px]">{detail || href}</span>
    </a>
  );
}

export default function BrandKitPage() {
  const printPage = () => window.print();

  return (
    <div className="min-h-screen bg-[#faf8f5] print:bg-white">
      {/* Print styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .break-inside-avoid { break-inside: avoid; }
          @page { margin: 1cm; size: A4; }
        }
      `}</style>

      {/* HEADER */}
      <div className="bg-gray-900 text-white py-8 px-4 print:py-4 print:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <Badge className="bg-[#ff6b35] text-white border-none mb-2 rounded-full px-3 py-1 text-[10px] print:hidden">
                <Printer className="w-3 h-3 mr-1" /> Scheda Completa per Tipografia
              </Badge>
              <h1 className="text-2xl md:text-3xl font-black font-google mb-1" data-testid="brandkit-title">
                <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
                <span className="text-white"> — Brand Kit Tipografia</span>
              </h1>
              <p className="text-gray-400 text-xs max-w-lg">
                Loghi, colori, font, specifiche prodotto, prezzi e link. Documento aggiornato — Marzo 2026.
              </p>
            </div>
            <Button onClick={printPage} size="sm" className="bg-[#ff6b35] hover:bg-[#e55a2b] text-white rounded-full text-xs print:hidden" data-testid="print-btn">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Stampa / Salva PDF
            </Button>
          </div>

          {/* Quick nav */}
          <div className="flex flex-wrap gap-2 mt-4 print:hidden">
            {[["loghi","Loghi"],["colori","Colori"],["font","Font"],["penne","Penne"],["stand","Stand"],["prezzi","Prezzi"],["link","Link"]].map(([id,l]) => (
              <a key={id} href={`#${id}`} className="text-[10px] bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-full transition-colors">{l}</a>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 print:py-4">

        {/* CONTATTO RAPIDO */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-7 shadow-sm break-inside-avoid">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0"><Phone className="w-3.5 h-3.5 text-green-600" /></div>
              <div><p className="font-bold text-gray-900">Riccardo Rossi</p><p className="text-gray-500">(+39) 327 383 9782</p></div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0"><MessageCircle className="w-3.5 h-3.5 text-green-600" /></div>
              <div><p className="font-bold text-gray-900">WhatsApp</p><p className="text-gray-500">wa.me/+393273839782</p></div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0"><Globe className="w-3.5 h-3.5 text-blue-600" /></div>
              <div><p className="font-bold text-gray-900">Sito Web</p><p className="text-gray-500 text-[10px] truncate">{SITE}</p></div>
            </div>
          </div>
        </div>

        {/* 1. LOGHI */}
        <Sec title="Loghi (PNG — Sfondo Trasparente)" icon={Image} id="loghi">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {LOGOS.map((l) => (
              <div key={l.name} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-4 flex items-center justify-center h-32" style={{ backgroundImage: "linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%), linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%)", backgroundSize: "12px 12px", backgroundPosition: "0 0, 6px 6px" }}>
                  <img src={l.url} alt={l.name} className="max-h-24 object-contain" />
                </div>
                <div className="p-2.5 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-[10px] font-bold text-gray-700">{l.name}</p>
                  <DL url={l.url} label="PNG" />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            Per incisione laser monocromatica: "schedabusiness" in Poppins Black (900), bianco su fondo colorato.
          </p>
        </Sec>

        {/* 2. COLORI */}
        <Sec title="Palette Colori e Codici" icon={Palette} id="colori">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Colori Google</p>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {COLORS_GOOGLE.map((c) => (
              <div key={c.hex} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="h-10 print:h-8" style={{ backgroundColor: c.hex }}></div>
                <div className="p-1.5">
                  <p className="text-[10px] font-bold text-gray-800">{c.name}</p>
                  <p className="text-[9px] text-gray-500 flex items-center">{c.hex}<CopyBtn text={c.hex} /></p>
                  <p className="text-[9px] text-gray-400">{c.pantone}</p>
                  <p className="text-[9px] text-gray-400">RGB {c.rgb}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Colori Brand schedabusiness</p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {COLORS_BRAND.map((c) => (
              <div key={c.hex} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="h-10 print:h-8" style={{ backgroundColor: c.hex }}></div>
                <div className="p-1.5">
                  <p className="text-[10px] font-bold text-gray-800">{c.name}</p>
                  <p className="text-[9px] text-gray-500 flex items-center">{c.hex}<CopyBtn text={c.hex} /></p>
                  <p className="text-[9px] text-gray-400">RGB {c.rgb}</p>
                  <p className="text-[9px] text-gray-400">{c.use}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Colori Penne (Alluminio Anodizzato)</p>
          <div className="grid grid-cols-4 gap-2">
            {PENS.map((p) => (
              <div key={p.hex} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                <div className="h-8" style={{ backgroundColor: p.hex }}></div>
                <div className="p-1.5">
                  <p className="text-[10px] font-bold text-gray-800">{p.name}</p>
                  <p className="text-[9px] text-gray-500">{p.hex} · {p.pantone}</p>
                  <p className="text-[9px] text-gray-400">RGB {p.rgb}</p>
                </div>
              </div>
            ))}
          </div>
        </Sec>

        {/* 3. FONT */}
        <Sec title="Tipografia" icon={Type} id="font">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <h3 className="text-xl font-black font-google text-gray-900">Poppins</h3>
              <p className="text-[10px] text-gray-500 mt-0.5">Font primario — Logo, titoli, branding</p>
              <p className="text-[10px] text-gray-400">Black (900) logo · Bold (700) titoli · Regular (400) corpo</p>
              <p className="text-[10px] text-gray-400">Licenza: Open Font License (uso commerciale OK)</p>
              <a href="https://fonts.google.com/specimen/Poppins" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-[#ff6b35] font-bold mt-2 print:text-gray-600">
                <ExternalLink className="w-3 h-3" /> fonts.google.com/specimen/Poppins
              </a>
              <div className="mt-3 p-2.5 bg-gray-50 rounded-lg">
                <p className="font-google text-lg font-black text-gray-900">
                  <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span> — Business Profile Solutions
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <h3 className="text-xl font-typewriter text-gray-900">Special Elite</h3>
              <p className="text-[10px] text-gray-500 mt-0.5">Font secondario — SOLO per Virtual Tour</p>
              <p className="text-[10px] text-gray-400">Stile macchina da scrivere, uso esclusivo Virtual Tour</p>
              <a href="https://fonts.google.com/specimen/Special+Elite" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] text-teal-600 font-bold mt-2 print:text-gray-600">
                <ExternalLink className="w-3 h-3" /> fonts.google.com/specimen/Special+Elite
              </a>
              <div className="mt-3 p-2.5 bg-gray-50 rounded-lg">
                <p className="font-typewriter text-lg text-teal-700">Virtual Tour 360</p>
              </div>
            </div>
          </div>
        </Sec>

        {/* 4. PENNE */}
        <Sec title="Penne schedabusiness — 4 Varianti" icon={PenLine} id="penne">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {PENS.map((p) => (
              <div key={p.hex} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="bg-gray-50 p-2"><img src={p.url} alt={`Penna ${p.name}`} className="w-full h-28 object-cover rounded-lg" /></div>
                <div className="p-2">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.hex }}></div>
                    <span className="text-[10px] font-bold text-gray-800">{p.name}</span>
                  </div>
                  <p className="text-[9px] text-gray-400">{p.hex} · {p.pantone}</p>
                  <DL url={p.url} label="HD" />
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <img src={ALL_PENS_IMG} alt="4 penne schedabusiness" className="w-full h-36 object-cover" />
              <div className="p-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-gray-700">Foto gruppo — 4 varianti</span>
                <DL url={ALL_PENS_IMG} />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <img src={BUNDLE_IMG} alt="Bundle schedabusiness" className="w-full h-36 object-cover" />
              <div className="p-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-gray-700">Bundle — Penne + Stand</span>
                <DL url={BUNDLE_IMG} />
              </div>
            </div>
          </div>

          {/* Specs penna */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm mb-3">
            <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Specifiche Penna a Sfera</p>
            </div>
            <table className="w-full text-[10px]">
              <tbody>
                {[
                  ["Tipo", "Penna a sfera retrattile (click)"],
                  ["Materiale corpo", "Alluminio anodizzato"],
                  ["Clip / punta", "Acciaio cromato"],
                  ["Refill", "Standard G2 (Parker-compatible), inchiostro nero"],
                  ["Dimensioni", "~140mm (L) x ~10mm (diametro)"],
                ].map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? "bg-gray-50/50" : ""}>
                    <td className="px-3 py-1.5 font-bold text-gray-700 w-1/3">{k}</td>
                    <td className="px-3 py-1.5 text-gray-600">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Specs incisione */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Specifiche Incisione Penne</p>
            </div>
            <table className="w-full text-[10px]">
              <tbody>
                {[
                  ["Tecnica", "Incisione laser"],
                  ["Testo", "\"schedabusiness\" — Font Poppins Black (900)"],
                  ["Colore incisione", "Bianco (contrasto su alluminio colorato)"],
                  ["Posizione", "Centro corpo, lato opposto alla clip"],
                  ["Area incisione", "~30mm (L) x 8mm (H), centrato orizzontale"],
                ].map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? "bg-gray-50/50" : ""}>
                    <td className="px-3 py-1.5 font-bold text-gray-700 w-1/3">{k}</td>
                    <td className="px-3 py-1.5 text-gray-600">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>

        {/* 5. STAND */}
        <Sec title="Base Smartphone in Bambu" icon={Smartphone} id="stand">
          <div className="grid grid-cols-2 gap-3 mb-3">
            {STAND_IMAGES.map((s) => (
              <div key={s.name} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <img src={s.url} alt={s.name} className="w-full h-36 object-cover" />
                <div className="p-2 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-gray-700">{s.name}</span>
                  <DL url={s.url} label="HD" />
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Specifiche Base Smartphone</p>
            </div>
            <table className="w-full text-[10px]">
              <tbody>
                {[
                  ["Riferimento", "Simile a IKEA BERGENES (art. 104.579.99)"],
                  ["Materiale", "Bambu naturale certificato FSC"],
                  ["Dimensioni", "~75mm (L) x 75mm (P) x 12mm (H)"],
                  ["Scanalatura", "~8mm profondita (smartphone + tablet)"],
                  ["Peso", "~80g"],
                  ["Incisione", "Laser — \"schedabusiness\" Poppins Black — frontale, ~20x10mm"],
                ].map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? "bg-gray-50/50" : ""}>
                    <td className="px-3 py-1.5 font-bold text-gray-700 w-1/3">{k}</td>
                    <td className="px-3 py-1.5 text-gray-600">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Sec>

        {/* 6. PREZZI */}
        <Sec title="Listino Prezzi Prodotti" icon={Package} id="prezzi">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm mb-3">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-3 py-2 text-left">Prodotto</th>
                  <th className="px-3 py-2 text-left">Quantita</th>
                  <th className="px-3 py-2 text-left">Prezzo al Pubblico</th>
                  <th className="px-3 py-2 text-left">Note</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Penna schedabusiness Blu (Start)", "a lotto", "REGALO con pacchetto Start", "Colore #3B82F6"],
                  ["Penna schedabusiness Rossa (Plus)", "a lotto", "REGALO con pacchetto Plus", "Colore #EF4444"],
                  ["Penna schedabusiness Ambra (Rooftop)", "a lotto", "REGALO con pacchetto Rooftop", "Colore #F59E0B"],
                  ["Penna schedabusiness Teal (Virtual Tour)", "a lotto", "REGALO con pacchetto Virtual Tour", "Colore #0D9488"],
                  ["100 Penne logo CLIENTE", "100 pz min.", "€199 + IVA", "Logo vettoriale richiesto"],
                  ["Base smartphone bambu schedabusiness", "a pezzo", "€29,99 IVA inclusa", "Incisione \"schedabusiness\" inclusa"],
                  ["BUNDLE: 100 Penne + Base", "on-demand", "€183,19 (-20%)", "-20% sul totale €228,99"],
                ].map(([a, b, c, d], i) => (
                  <tr key={a} className={i % 2 === 0 ? "bg-gray-50" : ""}>
                    <td className="px-3 py-2 font-bold text-gray-700">{a}</td>
                    <td className="px-3 py-2 text-gray-600">{b}</td>
                    <td className="px-3 py-2 text-gray-900 font-bold">{c}</td>
                    <td className="px-3 py-2 text-gray-500">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <strong>Penne logo CLIENTE:</strong> File logo vettoriale (SVG, AI, EPS). Monocromatico, max 8mm (H) x 35mm (L). Consegna: 15 gg lavorativi.
          </p>
        </Sec>

        {/* 7. LINK UTILI */}
        <Sec title="Link Utili — Risorse Online" icon={Link2} id="link">
          <div className="space-y-2">
            <LinkRow href={SITE} label="Sito Web schedabusiness" detail="Homepage principale" />
            <LinkRow href={`${SITE}/brand-kit`} label="Brand Kit Online" detail="Questa pagina" />
            <LinkRow href={`${SITE}/links`} label="schedabusiness Hub" detail="Tutti i link in un'unica pagina" />
            <LinkRow href={`${SITE}/start`} label="Pacchetto Start" detail="€99 — Verifica e claim scheda" />
            <LinkRow href={`${SITE}/plus`} label="Pacchetto Plus" detail="€299 — Sospensione e recensioni" />
            <LinkRow href={`${SITE}/rooftop`} label="Pacchetto Rooftop" detail="€599 — Tutto + assistenza premium 12 mesi" />
            <LinkRow href={`${SITE}/deducibile100`} label="Pacchetto Virtual Tour 360" detail="€499 — Virtual Tour + Start incluso" />
            <LinkRow href={`${SITE}/formazione`} label="SBPA Digital Academy" detail="€249 / €699 / €449+IVA" />
            <LinkRow href={`${SITE}/enti`} label="Partnership Enti/Associazioni" detail="Programma B2B dedicato" />
            <LinkRow href="https://fonts.google.com/specimen/Poppins" label="Font Poppins" detail="Google Fonts — download" />
            <LinkRow href="https://fonts.google.com/specimen/Special+Elite" label="Font Special Elite" detail="Google Fonts — Virtual Tour" />
            <LinkRow href="https://wa.me/+393273839782" label="WhatsApp Diretto" detail="+39 327 383 9782" />
          </div>

          {/* Image download links */}
          <div className="mt-4 bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
            <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-2">Download Immagini HD</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                ...LOGOS.map(l => ({ label: l.name, url: l.url })),
                ...PENS.map(p => ({ label: `Penna ${p.name}`, url: p.url })),
                { label: "4 Penne — Gruppo", url: ALL_PENS_IMG },
                { label: "Bundle Penne+Stand", url: BUNDLE_IMG },
                ...STAND_IMAGES.map(s => ({ label: s.name, url: s.url })),
              ].map(item => (
                <a key={item.url} href={item.url} target="_blank" rel="noopener noreferrer" download className="flex items-center gap-1.5 text-[10px] text-gray-600 hover:text-[#ff6b35] transition-colors py-1">
                  <Download className="w-3 h-3 flex-shrink-0 text-[#ff6b35]" />
                  <span className="truncate">{item.label}</span>
                </a>
              ))}
            </div>
          </div>
          {/* QR Code /scopri */}
          <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
            <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-3">QR Code — Esperienza Interattiva schedabusiness</p>
            <div className="inline-block bg-white rounded-xl p-3 border border-gray-200">
              <QRCodeSVG
                value={`${SITE}/scopri`}
                size={100}
                fgColor="#1a1a2e"
                bgColor="#ffffff"
                level="M"
              />
            </div>
            <p className="text-[10px] text-gray-500 mt-2">{SITE}/scopri</p>
            <p className="text-[9px] text-gray-400 mt-0.5">Scansiona per logo interattivo + menu completo</p>
          </div>
        </Sec>

        {/* FOOTER */}
        <div className="bg-gray-900 text-white rounded-xl p-5 text-center mt-4 break-inside-avoid">
          <p className="font-black text-sm font-google mb-0.5">
            <span className="text-[#EA4335]">S</span><span className="text-[#FBBC04]">B</span><span className="text-[#34A853]">P</span>
            <span className="text-white"> — Business Profile Solutions</span>
          </p>
          <p className="text-gray-400 text-xs">Riccardo Rossi · (+39) 327 383 9782 · WhatsApp · Siena</p>
          <p className="text-gray-600 text-[9px] mt-2">Documento riservato — Marzo 2026 — v2.0</p>
        </div>

      </div>
    </div>
  );
}
